/**
 * 메인(홈) 메뉴 해석 및 이동
 * - viewType A → /main.do#pgmId (AS-IS 풀 리로드)
 * - viewType T → TOBE path (SPA)
 * TheHeader 로고 클릭, error.vue 메인 이동 등에서 공용
 */
import { navigateWithAppLocale } from '~/composables/useAppLocale';

export const useHomeNavigation = () => {
  const menuStore = useMenuStore();
  const auth = useAuthStore();
  const { selectedMainPage } = useUserStore();
  const { navigateToAsis } = useNavigateToAsis();

  /** 메뉴 트리 leaf 목록 (사이드바/알림과 동일 순회) */
  const getAllMenuItems = () => {
    const allMenuItems: any[] = [];
    for (const section of menuStore.sections || []) {
      if (section.items?.length) {
        allMenuItems.push(...section.items);
      }
      for (const submenu of section.submenus || []) {
        if (submenu.items?.length) {
          allMenuItems.push(...submenu.items);
        }
      }
    }
    return allMenuItems;
  };

  const normalizeMenuPath = (rawPath: string) => {
    let targetPath = (rawPath || '').trim();
    if (!targetPath || targetPath === '#') return '';
    if (!targetPath.startsWith('/')) targetPath = `/${targetPath}`;
    targetPath = targetPath.replace(/^\/app(?=\/|$)/, '');
    targetPath = targetPath.replace(/^\/screens/, '');
    return targetPath.split('?')[0] || '';
  };

  const findMenuItemByPath = (targetPath: string) => {
    if (!targetPath || targetPath === '#') return null;
    const all = getAllMenuItems();
    const exact = all.find(item => item.path === targetPath);
    if (exact) return exact;
    const normalizedPath = normalizeMenuPath(targetPath);
    if (!normalizedPath) return null;
    return all.find(item => normalizeMenuPath(item.path) === normalizedPath) || null;
  };

  const findMenuItemByPgmId = (targetPgmId: string) => {
    if (!targetPgmId) return null;
    return getAllMenuItems().find(item => item.pgmId === targetPgmId) || null;
  };

  /** 메인페이지 설정 → 세션 mainPgm → 첫 메뉴 */
  const resolveHomeMenuItem = () => {
    if (selectedMainPage.value?.path) {
      const byPath = findMenuItemByPath(selectedMainPage.value.path);
      if (byPath) return byPath;
    }
    if (auth.user?.mainPgmId) {
      const byId = findMenuItemByPgmId(auth.user.mainPgmId);
      if (byId) return byId;
    }
    if (auth.user?.mainPgmPath) {
      const byPath = findMenuItemByPath(auth.user.mainPgmPath);
      if (byPath) return byPath;
    }
    return (
      getAllMenuItems().find(
        item => !item.disabled && (item.pgmId || (item.path && item.path !== '#')),
      ) || null
    );
  };

  /** 홈/메인 이동: viewType A → /main.do#pgmId, T → TOBE path */
  const navigateToHome = async () => {
    const item = resolveHomeMenuItem();
    if (!item) {
      await navigateWithAppLocale('/guide');
      return;
    }

    if (item.viewType === 'A') {
      navigateToAsis({
        entryPath: '/main.do',
        hash: item.pgmId || item.menuId || '',
      });
      return;
    }

    if (item.path && item.path !== '#') {
      await navigateWithAppLocale(item.path);
    }
  };

  return {
    getAllMenuItems,
    resolveHomeMenuItem,
    navigateToHome,
  };
};
